import { Navigate, Route, Routes } from 'react-router-dom';
import RequireRole from './auth/RequireRole';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Team from './pages/Team';
import TeamMemberDetail from './pages/TeamMemberDetail';
import Plants from './pages/Plants';
import PlantDetail from './pages/PlantDetail';
import Suppliers from './pages/Suppliers';
import SupplierDetail from './pages/SupplierDetail';
import Parts from './pages/Parts';
import PartDetail from './pages/PartDetail';
import Reports from './pages/Reports';
import Billing from './pages/Billing';
import InvoicesLayout from './pages/InvoicesLayout';
import InvoiceTracker from './pages/InvoiceTracker';
import NewInvoices from './pages/NewInvoices';
import SentInvoices from './pages/SentInvoices';
import InvoiceDraft from './pages/InvoiceDraft';
import InvoiceDetail from './pages/InvoiceDetail';
import PoTracker from './pages/PoTracker';
import AccountStatements from './pages/AccountStatements';
import Sales from './pages/Sales';
import Timesheets from './pages/Timesheets';
import TimesheetApprovals from './pages/TimesheetApprovals';
import TimesheetsLayout from './pages/TimesheetsLayout';
import Adjustments from './pages/Adjustments';
import MyAccount from './pages/MyAccount';

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />

      <Route
        element={
          <RequireRole>
            <Layout />
          </RequireRole>
        }
      >
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/team" element={<Team />} />
        <Route path="/team/:id" element={<TeamMemberDetail />} />
        <Route path="/plants" element={<Plants />} />
        <Route path="/plants/:plantCode" element={<PlantDetail />} />
        <Route path="/suppliers" element={<Suppliers />} />
        <Route path="/suppliers/:supplierCode" element={<SupplierDetail />} />
        <Route path="/parts" element={<Parts />} />
        <Route path="/parts/:partCode" element={<PartDetail />} />
        <Route path="/reports" element={<Reports />} />
        {/* Billing Manager — Director-only. Gated at the route so typing the
            URL directly is blocked, matching the sidebar link guard. */}
        <Route
          path="/billing"
          element={
            <RequireRole roles={['Director']}>
              <Billing />
            </RequireRole>
          }
        />

        {/* Invoices — Director-only — three tabs (Invoice Tracker /
            New Invoices / Sent Invoices) live under the parent route.
            The InvoicesLayout renders the tab strip + redirects bare
            /invoices to the Invoice Tracker default. */}
        <Route
          path="/invoices"
          element={
            <RequireRole roles={['Director']}>
              <InvoicesLayout />
            </RequireRole>
          }
        >
          <Route index element={null /* redirect handled in layout */} />
          <Route path="tracker" element={<InvoiceTracker />} />
          <Route path="new" element={<NewInvoices />} />
          <Route path="sent" element={<SentInvoices />} />
          {/* PO Tracker — Blanket PO drawdown per supplier. */}
          <Route path="pos" element={<PoTracker />} />
          {/* Account Statements — per-customer Aged AR view of every
              outstanding invoice (derived from the invoices list). */}
          <Route path="statements" element={<AccountStatements />} />
        </Route>
        {/* Submit Invoice draft page — opens from the New Invoices
            tab when the Director clicks "Submit Invoice" on a
            supplier row. Lives at /invoices/draft/:supplierCode so
            it doesn't collide with the /invoices/new tab. Director-
            only same as the parent route. */}
        <Route
          path="/invoices/draft/:supplierCode"
          element={
            <RequireRole roles={['Director']}>
              <InvoiceDraft />
            </RequireRole>
          }
        />

        {/* Blank invoice — a from-scratch invoice not tied to any
            Billing Tracker timesheets. Opens from the "Create Blank
            Invoice" button on the New Invoices tab. Same draft form,
            but the Director picks the supplier and fills every line in
            by hand. Director-only. */}
        <Route
          path="/invoices/blank"
          element={
            <RequireRole roles={['Director']}>
              <InvoiceDraft blank />
            </RequireRole>
          }
        />

        {/* Invoice detail — editable view of a saved invoice, reached
            by clicking an invoice number on the Sent Invoices or
            Invoice Tracker tabs. /invoices/view/:id so it doesn't
            collide with the tab routes or the draft page. Director-
            only same as the parent route. */}
        <Route
          path="/invoices/view/:id"
          element={
            <RequireRole roles={['Director']}>
              <InvoiceDetail />
            </RequireRole>
          }
        />

        {/* Sales houses customer agreements/proposals and the downloadable
            sales-resource library. Gated by the canSeeSales capability (all
            Directors, plus anyone granted a Sales override in
            accessOverrides.js) so link-level guarding isn't the only barrier.
            Note: the Director-only resource-upload library inside the page is
            gated separately. */}
        <Route
          path="/sales"
          element={
            <RequireRole capability="canSeeSales">
              <Sales />
            </RequireRole>
          }
        />

        {/* Timesheets — two tabs (Submit / Approvals) live under one
            parent route so the previously-separate "Timesheets" and
            "Timesheet Approvals" sidebar items can collapse into a
            single section. TimesheetsLayout renders the tab strip +
            handles the role-aware default redirect; each tab's page
            gates its own permissions inline. Existing deep links —
            /timesheets/submit?edit=123, ?supplement-of=456,
            ?credit-of=789 — keep working. The Approvals tab shows
            every Pending / Approved / Rejected timesheet including
            supplements and credits. */}
        <Route path="/timesheets" element={<TimesheetsLayout />}>
          <Route index element={null /* redirect handled in layout */} />
          <Route path="submit" element={<Timesheets />} />
          <Route path="approvals" element={<TimesheetApprovals />} />
          {/* Liaison Managers' read-only "Approved Trackers" tab — the
              same page + search bar as Approvals, scoped to their own
              approved submissions (per Jillian, July 2026). */}
          <Route
            path="approved"
            element={<TimesheetApprovals selfApprovedView />}
          />
          {/* Adjustments — Director-only (per Jillian, Aug 2026). RMs
              who deep-link here are bounced to /dashboard by RequireRole. */}
          <Route
            path="adjustments"
            element={
              <RequireRole roles={['Director']}>
                <Adjustments />
              </RequireRole>
            }
          />
        </Route>

        {/* Open to every signed-in team member — "My Account" hub for
            self-service requests (vest/shirt/card/badge/address change)
            and their status. Directors see the full inbox on the same
            page. */}
        <Route path="/my-account" element={<MyAccount />} />

        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Route>
    </Routes>
  );
}
